import type {Placement} from '@popperjs/core';
import React, {FC, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ReplyFactModel} from '../../../../../libs/shared/core/src/models/api/factModel';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {basicMarkdownStyle} from '../../../../../libs/shared/ui/src/styles/markdownStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {MarkdownRenderer} from '../../../../../srcCommon/formatters/markdown/markdownRenderer';
import {markdownDomComponents} from '../../../common/formatters/markdown/markdownDomComponents';
import {TriggerPreview} from './triggerPreview';

interface AiReplyPreviewProps {
  anchor: RefObject<HTMLElement>;
  popperPlacement: Placement;
  aiReply: ReplyFactModel;
}

const StyledOutlineDiv = styled.div`
  line-height: 1.4;
  &,
  * {
    cursor: text;
    user-select: text;
  }

  ${basicMarkdownStyle()}

  ul, ol {
    margin: 0;
    padding-inline-start: 12px;
  }

  code {
    white-space: pre-wrap;
  }
`;

const StyledFooter = styled.footer`
  padding: 6px 4px;
`;

const StyledButton = styled(Button)`
  display: flex;
`;

const StyledButtonContentDiv = styled.div`
  ${ellipsis()};
`;

export const AiReplyPreview: FC<AiReplyPreviewProps> = ({anchor, popperPlacement, aiReply}) => (
  <TriggerPreview
    anchor={anchor}
    popperPlacement={popperPlacement}
    header={<Trans id="suggested-reply-title">Suggested reply</Trans>}
    content={
      <StyledOutlineDiv>
        <MarkdownRenderer
          debugIdentifier="ai-reply-preview"
          text={aiReply.outline}
          components={markdownDomComponents}
        />
      </StyledOutlineDiv>
    }
    footer={
      <StyledFooter>
        {aiReply.references.map(({name, href}) => (
          <StyledButton key={href} variant={ButtonVariantsEnum.LINK} onClick={() => openInBrowser(href)}>
            <StyledButtonContentDiv>{name}</StyledButtonContentDiv>
          </StyledButton>
        ))}
      </StyledFooter>
    }
  />
);
